import { memo, useCallback, useMemo } from 'react';
import { Autocomplete } from '@mui/material';
import { __OPERATION__BATCH__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import PmiTextField from 'shell/components/shared/text-field';
import { ResponseAbortable } from 'shell/hooks/use-http-fetch/stateless/type';
import debounce from 'shell/utils/debounce';
import { Job } from 'shell/models/job-item';
import { AxiosResponse } from 'axios';
import { SearchField } from './constants';
import { JobVM } from 'system/view-models/batch-managment/job-vm';

interface SearchBatchListProps {
  disabled?: boolean;
  value: JobVM | null;
  onChange: (item: JobVM | null) => void;
  error?: boolean;
  helperText?: string;
  id?: string;
  label?: string;
  searchField: SearchField;
}

const SearchBatchListComponent = ({
  disabled,
  onChange,
  value,
  error,
  helperText,
  id,
  label,
  searchField,
}: SearchBatchListProps): React.ReactElement => {
  const [jobs, getJobs] = useHttpFetch<HttpResponseList<Job>, Array<JobVM>>(
    `${__OPERATION__BATCH__}/jobs`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: [],
      },
      transformer: (response: AxiosResponse<HttpResponseList<Job>>) => {
        return response.data.payload.list?.map((item) => {
          const res: JobVM = {
            id: item?.name,
            description: item?.description || '',
            enabled: item?.enabled || false,
            executorLocation: item?.executorLocation || '',
            executorName: item?.executorName || '',
            inputFolder: item?.nmCartellaInput || '',
            name: item?.name || '',
            outputFolder: item?.nmCartellaOutput || '',
          };
          return res;
        });
      },
    }
  );

  const jobsData = (jobs.data as Array<JobVM>) || [];

  const handleChange = (element: JobVM | null): void => {
    onChange(element);
  };

  const defaultProps = useMemo(() => {
    const items = jobsData.slice() || [];

    return {
      options: items,
      getOptionLabel: (option: JobVM) => option?.id?.toString(),
    };
  }, [jobsData]);

  const defaultId = useMemo(() => {
    if (id) return id;
    return searchField === SearchField.JOB_DESCRIPTION
      ? 'batch-description-autocomplete'
      : 'batch-name-autocomplete';
  }, [id, searchField]);

  const defaultLabel = useMemo(() => {
    if (label) return label;
    return searchField === SearchField.JOB_DESCRIPTION ? 'Descrizione' : 'Nome Batch';
  }, [label, searchField]);

  const searchParam = useMemo(() => {
    return searchField === SearchField.JOB_DESCRIPTION
      ? SearchField.JOB_DESCRIPTION
      : SearchField.JOB_NAME;
  }, [searchField]);

  const handleSearch = debounce<string>(
    useCallback(
      (searchValue: string) => {
        if (jobs.abort) {
          jobs.abort();
        }

        getJobs({
          params: {},
          paramsSerializer: () => `${searchParam}=${searchValue}`,
        }) as ResponseAbortable<HttpResponseList<Job>, Array<JobVM>>;
      },
      [getJobs, jobs.abort, searchParam]
    )
  );

  return (
    <Autocomplete
      {...defaultProps}
      value={value}
      id={defaultId}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      onChange={(event, element) => {
        event.stopPropagation();
        handleChange(element);
      }}
      disabled={disabled}
      clearOnBlur={false}
      onInputChange={(event, changeValue, reason) => {
        if (changeValue.length >= 3 && reason === 'input') {
          handleSearch(changeValue);
        }
      }}
      loading={jobs.isLoading}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          label={defaultLabel}
          variant="outlined"
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};

SearchBatchListComponent.displayName = 'Search batch list autocomplete component';

const SearchBatchList = memo(SearchBatchListComponent);

export default SearchBatchList as typeof SearchBatchListComponent;
